import { useState, useEffect, useMemo, useContext, forwardRef } from "react";
import {
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Grid,
  Alert,
  Tabs,
  Tab,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

// NEW: step components
import EventDetailsTab from "./components/EventDetailsTab";
import TicketingStep from "./components/TicketingStep";
import Ticketing from "./components/Ticketing";
import Messaging from "./components/Messaging";
import PromoTab from "./components/PromoTab";

dayjs.extend(utc);
dayjs.extend(timezone);

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const NewEventDialog = ({ open, close, eventData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userData } = useContext(EventContext);
  const orgTz = useMemo(() => {
    return userData?.org?.timezone || "America/Los_Angeles";
  }, [eventData, userData]);
  const [ignoreValidations, setIgnoreValidations] = useState(false);
  const [event, setEvent] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [showTicketing, setShowTicketing] = useState(false);
  const [eventChannels, setEventChannels] = useState([]);

  // Event details
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [listed, setListed] = useState(false);
  const [start, setStart] = useState(dayjs().tz(orgTz));
  const [end, setEnd] = useState(dayjs().tz(orgTz));
  const [deployment, setDeployment] = useState(null);

  // Venue
  const [venueName, setVenueName] = useState("");
  const [venueStreetAddress, setVenueStreetAddress] = useState("");
  const [venueCity, setVenueCity] = useState("");
  const [venueRegion, setVenueRegion] = useState("");
  const [venuePostalCode, setVenuePostalCode] = useState("");

  // Ticketing
  const [capacity, setCapacity] = useState("");
  const [canRegisterInApp, setCanRegisterInApp] = useState(false);
  const [registrationLink, setRegistrationLink] = useState("");
  const [eventType, setEventType] = useState("");
  const [questionGroup, setQuestionGroup] = useState("");
  const [questionGroups, setQuestionGroups] = useState([]);

  // Image
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);

  // UI
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (eventData) setEvent(eventData);
  }, [eventData]);

  useEffect(() => {
    if (userData?.org?.config?.ticketingEnabled) {
      setShowTicketing(true);
    }
  }, [userData]);

  useEffect(() => {
    if (event && event?.source !== "tnfm") {
      setIgnoreValidations(true);
    }
  }, [event]);

  useEffect(() => {
    if (open) {
      getQuestionGroups();
      getDeployments();
      if (event) {
        getChannels();
      }
    }
  }, [open]);

  useEffect(() => {
    if (open && event && !ignoreValidations) {
      uploadImage(event._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile]);

  const toUtcIsoFromOrg = (d) => {
    if (!d) return null;
    return d.tz(orgTz).utc().toISOString();
  };

  const getDeployments = async () => {
    const result = await request("orgs/deployments");
    if (result.success) {
      setDeployments(result.data);
    }
  };

  const getQuestionGroups = async () => {
    const result = await request("questiongroups");
    if (result.success) {
      setQuestionGroups(result.questionGroups);
    }
  };

  const getChannels = async () => {
    const result = await request(`events/eventchannel/?eventId=${event?._id}`);
    if (result.success) {
      setEventChannels(result.data);
    }
  };

  // Image validation/preview
  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImageFile(file);
    if (!file) {
      setImagePreview(null);
      setImageBase64(null);
      return;
    }
    const img = new window.Image();
    img.onload = function () {
      const aspectRatio = this.width / this.height;
      if (Math.abs(aspectRatio - 2) > 0.01) {
        let err = `Image should have a 2:1 aspect ratio (e.g., 2160x1080, 940x470). Selected image is ${this.width} x ${this.height}.`;
        setError(err);
        toast.error(err);

        setImageFile(null);
        setImagePreview(null);
        setImageBase64(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        setImagePreview(result);
        const commaIdx = result.indexOf(",");
        setImageBase64(
          commaIdx !== -1 ? result.substring(commaIdx + 1) : result
        );
      };
      reader.readAsDataURL(file);
    };
    img.onerror = function () {
      let err = "Could not load image for dimension check.";
      setError(err);
      toast.error(err);
      setImageFile(null);
      setImagePreview(null);
      setImageBase64(null);
    };
    img.src = URL.createObjectURL(file);
  };

  const uploadImage = async (eventId) => {
    const body = new FormData();
    body.append("file", imageFile);
    body.append("Content-Type", "image/jpg");
    body.append("eventId", eventId);

    const token = localStorage.getItem(
      `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
    );
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_TNFT_SERVER}/events/uploadimage`,
        method: "post",
        data: body,
        headers: { "Content-Type": "multipart/form-data", Accesstoken: token },
      });
      if (response?.data?.success) {
        toast.success("Event image uploaded");
        return true;
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error);
      return false;
    }
  };

  // Initialize/reset on open
  useEffect(() => {
    if (open && event) {
      setError(null);
      setActiveTab(0);
      setName(event?.name);
      setDescription(event?.description);
      setListed(!!event?.listed);
      const localTz = dayjs.tz.guess();
      setStart(
        event?.start
          ? dayjs.utc(event.start).tz(orgTz)
          : dayjs().tz(localTz)
      );
      setEnd(
        event?.end
          ? dayjs.utc(event.end).tz(orgTz)
          : dayjs().tz(localTz)
      );
      setVenueName(event?.venueName);
      setVenueStreetAddress(event?.venueAddress1);
      setVenueCity(event?.venueCity);
      setVenueRegion(event?.venueRegion);
      setVenuePostalCode(event?.venuePostalCode);
      setCapacity(event?.capacity);
      setCanRegisterInApp(event?.canRegisterInApp);
      setRegistrationLink(
        event?.registrationLink || event?.eventbriteUrl || ""
      );
      setImagePreview(event?.imageUrl);
      setDeployment(event?.deployment);
    } else if (open) {
      setError(null);
      setActiveTab(0);
      setName("");
      setDescription("");
      setListed(false);
      const localTz = dayjs.tz.guess();
      setStart(dayjs().tz(localTz).add(10, "day"));
      setEnd(dayjs().tz(localTz).add(10, "day").add(3, "hour"));
      setVenueName("");
      setVenueStreetAddress("");
      setVenueCity("");
      setVenueRegion("");
      setVenuePostalCode("");
      setCapacity("");
      setCanRegisterInApp(false);
      setRegistrationLink("");
      setImageFile(null);
      setImagePreview(null);
      setImageBase64(null);
      setEventType("");
      setQuestionGroup("");
      setQuestionGroups([]);
      setEventChannels([]);
    }
  }, [open, event]);

  const handleSave = async () => {
    let err;
    if (!ignoreValidations) {
      if (!name) err = "Please enter a name";
      else if (!description) err = "Please enter a description";
      else if (!start) err = "Please enter a start date/time";
      else if (!end) err = "Please enter an end date/time";
    }

    if (!err && !ignoreValidations) {
      const now = dayjs().tz(orgTz);
      if (!dayjs(start).isValid() || !dayjs(end).isValid()) {
        err = "Please enter valid start and end date/times";
      } else if (!dayjs(start).isAfter(now)) {
        err = "Start date/time must be in the future";
      } else if (!dayjs(end).isAfter(now)) {
        err = "End date/time must be in the future";
      } else if (!dayjs(end).isAfter(dayjs(start))) {
        err = "End date/time must be after start";
      }
    }
    if (!err && !venueName) err = "Please enter a venue name";
    // else if (!err && !capacity) err = "Please enter a total capacity";
    // if (!err && canRegisterInApp === false && !registrationLink) {
    //   err = "Please enter a registration link or allow in-app registration";
    // }
    if (capacity !== "") {
      const asInt = Number(capacity);
      if (!Number.isInteger(asInt) || asInt < 0)
        err = "Capacity must be a whole number";
    }
    if (err) {
      setError(err);
      toast.error(err);
      return;
    } else {
      setError(null);
    }

    if (event) {
      const body = {
        eventId: event._id,
        update: {
          name,
          description,
          listed,
          start: toUtcIsoFromOrg(start),
          end: toUtcIsoFromOrg(end),
          date: toUtcIsoFromOrg(start),
          venueName: venueName || undefined,
          venueAddress1: venueStreetAddress || undefined,
          venueCity: venueCity || undefined,
          venueRegion: venueRegion || undefined,
          venuePostalCode: venuePostalCode || undefined,
          capacity: capacity === "" ? undefined : Number(capacity),
          canRegisterInApp,
          registrationLink: registrationLink || undefined,
        },
      };
      const updateResult = await request("events/update", "post", body);
      if (updateResult.success) {
        toast.success("Event updated successfully");
        close();
      } else {
        let err = updateResult.error || "Failed to update event";
        setError(err);
        toast.error(err);
      }
    } else {
      const body = {
        eventData: {
          name,
          description,
          listed,
          start: toUtcIsoFromOrg(start),
          end: toUtcIsoFromOrg(end),
          date: toUtcIsoFromOrg(start),
          venueName: venueName || undefined,
          venueAddress1: venueStreetAddress || undefined,
          venueCity: venueCity || undefined,
          venueRegion: venueRegion || undefined,
          venuePostalCode: venuePostalCode || undefined,
          capacity: capacity === "" ? undefined : Number(capacity),
          canRegisterInApp,
          registrationLink: registrationLink || undefined,
          deployment,
        },
        pairingEventType: eventType,
        questionGroup,
      };

      const result = await request("events/create", "post", body);
      if (!result.success) {
        let err = result.error || "Failed to create event";
        setError(err);
        toast.error(err);
        return;
      } else {
        setEvent(result.event);
        if (imageFile) {
          toast.success("Event created. Uploading image");
          const uploadResult = await uploadImage(result.event?._id);
          if (uploadResult) close();
        } else {
          toast.success("Event created successfully. ");
        }
      }
    }
  };

  return (
    <div>
      <Dialog
        fullScreen
        open={open}
        onClose={close}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={close}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {event ? "Edit Event" : "New Event"}
            </Typography>
            {event && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={ignoreValidations}
              >
                save
              </Button>
            )}
          </Toolbar>
        </AppBar>

        <Grid
          container
          direction="column"
          spacing={3}
          style={{ padding: 20, marginLeft: 0 }}
        >
          {error ? (
            <Grid item>
              <Alert severity="error">{error}</Alert>
            </Grid>
          ) : null}

          <Grid item>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              aria-label="event tabs"
            >
              <Tab label="Event Details" />
              {showTicketing && <Tab label="Ticketing" disabled={!event} />}
              {showTicketing && <Tab label="Messaging" disabled={!event} />}
              {showTicketing && <Tab label="Promotions" disabled={!event} />}
            </Tabs>
          </Grid>

          {/* Tabs content */}
          {activeTab === 0 && (
            <EventDetailsTab
              dayjs={dayjs}
              orgTz={orgTz}
              isMobile={isMobile}
              showTicketing={showTicketing}
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              listed={listed}
              setListed={setListed}
              start={start}
              setStart={setStart}
              end={end}
              setEnd={setEnd}
              imageFile={imageFile}
              imagePreview={imagePreview}
              onImageChange={handleImageChange}
              event={event}
              venueName={venueName}
              setVenueName={setVenueName}
              venueStreetAddress={venueStreetAddress}
              setVenueStreetAddress={setVenueStreetAddress}
              venueCity={venueCity}
              setVenueCity={setVenueCity}
              venueRegion={venueRegion}
              setVenueRegion={setVenueRegion}
              venuePostalCode={venuePostalCode}
              setVenuePostalCode={setVenuePostalCode}
              capacity={capacity}
              setCapacity={setCapacity}
              eventType={eventType}
              setEventType={setEventType}
              questionGroup={questionGroup}
              setQuestionGroup={setQuestionGroup}
              questionGroups={questionGroups}
              deployments={deployments}
              deployment={deployment}
              setDeployment={setDeployment}
              registrationLink={registrationLink}
              setRegistrationLink={setRegistrationLink}
            />
          )}

          {activeTab === 1 &&
            event &&
            (showTicketing ? (
              <Ticketing
                isMobile={isMobile}
                capacity={capacity}
                setCapacity={setCapacity}
                canRegisterInApp={canRegisterInApp}
                setCanRegisterInApp={setCanRegisterInApp}
                registrationLink={registrationLink}
                setRegistrationLink={setRegistrationLink}
                event={event}
                eventType={eventType}
                setEventType={setEventType}
                questionGroup={questionGroup}
                setQuestionGroup={setQuestionGroup}
                questionGroups={questionGroups}
                start={start}
                eventChannels={eventChannels}
              />
            ) : (
              <TicketingStep
                isMobile={isMobile}
                capacity={capacity}
                setCapacity={setCapacity}
                canRegisterInApp={canRegisterInApp}
                setCanRegisterInApp={setCanRegisterInApp}
                registrationLink={registrationLink}
                setRegistrationLink={setRegistrationLink}
                event={event}
                eventType={eventType}
                setEventType={setEventType}
                questionGroup={questionGroup}
                setQuestionGroup={setQuestionGroup}
                questionGroups={questionGroups}
              />
            ))}

          {activeTab === 2 &&
            event &&
            (showTicketing ? <Messaging event={event} /> : null)}

          {activeTab === 3 &&
            event &&
            (showTicketing ? <PromoTab event={event} /> : null)}

          {/* Navigation */}
          {activeTab === 0 ? (
            <Grid item sx={{ p: 3 }}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={ignoreValidations}
                >
                  {event ? "Save Event" : "Create Event"}
                </Button>
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </Dialog>
    </div>
  );
};

export default NewEventDialog;
